import { __rest, __read, __awaiter, __generator, __assign } from 'tslib';
import React__default from 'react';
import debounce from 'debounce-promise';
import isEqual from 'lodash/isEqual';
import { useAnalyticsQueue } from '@invitae/analytics-queue';
import { CartErrorsValidationCodeEnum } from '@invitae/stargate-ts-fetch';
import { getClients } from '../api/index.esm.js';
import { AnalyticsEventName } from '../constants/analytics.esm.js';
import { HttpCodeEnum } from '../constants/api.esm.js';
import { isUserAuthenticated } from '../utils/isUserAuthenticated.esm.js';
import { isServer } from '../utils/misc.esm.js';
import { wait } from '../utils/wait.esm.js';
import { copyDefaultContext, PARTNERSHIP_CODE_REMOVED_MESSAGE, PARTNERSHIP_CODE_NOT_ADDED_MESSAGE } from './CartContext.utils.esm.js';

var FETCH_DEBOUNCE_DELAY = 250;
var PARTNERSHIP_WARNING_DISPLAY_TIME = 5000;
var PARTNERSHIP_WARNING_TRANSITION_DURATION = 1000;
/**
 * Hook to create a CartContext.
 */
var useMakeCartContext = function (_a) {
    var _b, _c, _d, _e;
    var apiParameters = _a.apiParameters, gotoCartPage = _a.gotoCartPage, goToSignInPage = _a.goToSignInPage, featureFlags = _a.featureFlags, _f = _a.isUserAuthenticated, isUserAuthenticated$1 = _f === void 0 ? false : _f, _g = _a.showPatientInfoBanner, showPatientInfoBanner = _g === void 0 ? true : _g, options = __rest(_a, ["apiParameters", "gotoCartPage", "goToSignInPage", "featureFlags", "isUserAuthenticated", "showPatientInfoBanner"]);
    var _h = __read(React__default.useState(), 2), cart = _h[0], setCart = _h[1];
    var _j = __read(React__default.useState(), 2), errors = _j[0], setErrors = _j[1];
    var _k = __read(React__default.useState(), 2), meta = _k[0], setMeta = _k[1];
    var _l = __read(React__default.useState(false), 2), shouldKeepMiniCartOpen = _l[0], setShouldKeepMiniCartOpen = _l[1];
    var _m = __read(React__default.useState({}), 2), customModalProps = _m[0], setCustomModalProps = _m[1];
    var _o = __read(React__default.useState({}), 2), reflexValidationErrors = _o[0], setReflexValidationErrors = _o[1];
    var _p = __read(React__default.useState(false), 2), rereqValidationError = _p[0], setRereqValidationError = _p[1];
    var _q = __read(React__default.useState(''), 2), partnershipToAdd = _q[0], setPartnershipToAdd = _q[1];
    var _r = __read(React__default.useState(false), 2), requestPending = _r[0], setRequestPending = _r[1];
    var _s = __read(React__default.useState(), 2), partnershipWarning = _s[0], setPartnershipWarning = _s[1];
    var _t = __read(React__default.useState(false), 2), shouldShowPartnershipWarning = _t[0], setShouldShowPartnershipWarning = _t[1];
    var _u = __read(React__default.useState(false), 2), hasInvalidExomeAddOnConfiguration = _u[0], setHasInvalidExomeAddOnConfiguration = _u[1];
    var logEvent = useAnalyticsQueue().logEvent;
    var _v = featureFlags || {}, _w = _v.cx_custom_rna_hcx, isCxCustomRnaHcxEnabled = _w === void 0 ? false : _w, _x = _v["cx-retain-custom-panel-context"], isCxRetainCustomPanelContextEnabled = _x === void 0 ? false : _x;
    var _y = getClients(apiParameters), cartApiClient = _y.cartApiClient, partnershipApiClient = _y.partnershipApiClient, reflexApiClient = _y.reflexApiClient, rereqApiClient = _y.rereqApiClient;
    var isUserAuth = React__default.useMemo(function () { return isUserAuthenticated() || isUserAuthenticated$1; }, [isUserAuthenticated$1]);
    var portalUser = isServer() ? {} : (_b = window === null || window === void 0 ? void 0 : window.PORTAL) === null || _b === void 0 ? void 0 : _b.user;
    // There should only be one order group for orders with RNA analysis
    var _z = ((_e = (_d = (_c = cart === null || cart === void 0 ? void 0 : cart.orderGroups) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.meta) === null || _e === void 0 ? void 0 : _e.rna) || {}, _0 = _z.isParentRnaAnalysisAvailable, isParentRnaAnalysisAvailable = _0 === void 0 ? false : _0, _1 = _z.isParentRnaAnalysisSelected, isParentRnaAnalysisSelected = _1 === void 0 ? false : _1, _2 = _z.isReflexRnaAnalysisAvailable, isReflexRnaAnalysisAvailable = _2 === void 0 ? false : _2, _3 = _z.isReflexRnaAnalysisSelected, isReflexRnaAnalysisSelected = _3 === void 0 ? false : _3;
    var safelyCallApiClient = React__default.useCallback(function (clientCall, showLoadingOverlay) {
        if (showLoadingOverlay === void 0) { showLoadingOverlay = true; }
        return __awaiter(void 0, void 0, void 0, function () {
            var e_1, error, errorMessage, _a;
            var _b, _c, _d;
            return __generator(this, function (_e) {
                switch (_e.label) {
                    case 0:
                        if (!isUserAuth) {
                            goToSignInPage();
                            return [2 /*return*/, { cart: cart, errors: errors, meta: meta }];
                        }
                        if (showLoadingOverlay)
                            setRequestPending(true);
                        _e.label = 1;
                    case 1:
                        _e.trys.push([1, 3, 7, 8]);
                        return [4 /*yield*/, clientCall()];
                    case 2: return [2 /*return*/, (_e.sent())];
                    case 3:
                        e_1 = _e.sent();
                        error = e_1;
                        if (((_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.status) === HttpCodeEnum.HTTP_401_UNAUTHORIZED) {
                            goToSignInPage();
                            return [2 /*return*/, { cart: cart, errors: errors, meta: meta }];
                        }
                        if (!(((_c = error === null || error === void 0 ? void 0 : error.response) === null || _c === void 0 ? void 0 : _c.status) !== HttpCodeEnum.HTTP_500_INTERNAL_SERVER_ERROR && ((_d = error === null || error === void 0 ? void 0 : error.response) === null || _d === void 0 ? void 0 : _d.text))) return [3 /*break*/, 5];
                        return [4 /*yield*/, error.response.text()];
                    case 4:
                        _a = _e.sent();
                        return [3 /*break*/, 6];
                    case 5:
                        _a = undefined;
                        _e.label = 6;
                    case 6:
                        errorMessage = _a;
                        return [2 /*return*/, {
                                cart: cart,
                                errors: { errorMessage: errorMessage, validationCode: CartErrorsValidationCodeEnum.Unknown },
                                meta: meta,
                            }];
                    case 7:
                        if (showLoadingOverlay)
                            setRequestPending(false);
                        return [7 /*endfinally*/];
                    case 8: return [2 /*return*/];
                }
            });
        });
    }, [cart, errors, meta, isUserAuth]);
    var setNewCartState = React__default.useCallback(function (newCartState) {
        var cart = newCartState.cart, errors = newCartState.errors, meta = newCartState.meta;
        setCart(cart);
        setErrors(errors);
        setMeta(meta);
    }, [setCart, setErrors, setMeta]);
    var getCart = React__default.useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _a = setNewCartState;
                    return [4 /*yield*/, safelyCallApiClient(function () {
                            return cartApiClient.getCart1();
                        }, false)];
                case 1:
                    _a.apply(void 0, [_b.sent()]);
                    return [2 /*return*/];
            }
        });
    }); }, [cartApiClient, setNewCartState, safelyCallApiClient]);
    var showPartnershipWarning = React__default.useCallback(function (message) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    // Allow showing partnership warning and set appropriate message to show.
                    setShouldShowPartnershipWarning(true);
                    setPartnershipWarning(message);
                    // Initiate "hide" animation after warning was shown for required amount of time.
                    return [4 /*yield*/, wait(PARTNERSHIP_WARNING_DISPLAY_TIME)];
                case 1:
                    // Initiate "hide" animation after warning was shown for required amount of time.
                    _a.sent();
                    setShouldShowPartnershipWarning(false);
                    // Once transition is finished clear warning message.
                    return [4 /*yield*/, wait(PARTNERSHIP_WARNING_TRANSITION_DURATION)];
                case 2:
                    // Once transition is finished clear warning message.
                    _a.sent();
                    setPartnershipWarning(undefined);
                    return [2 /*return*/];
            }
        });
    }); }, []);
    var addToCart = React__default.useCallback(function (conceptCodes, options, orderContext) { return __awaiter(void 0, void 0, void 0, function () {
        var response, isPartnershipRemoved;
        var _a, _b, _c, _d, _e, _f, _g;
        return __generator(this, function (_h) {
            switch (_h.label) {
                case 0: return [4 /*yield*/, safelyCallApiClient(function () {
                        var _a;
                        return ((_a = meta === null || meta === void 0 ? void 0 : meta.rereq) === null || _a === void 0 ? void 0 : _a.isRerequisition)
                            ? rereqApiClient.addRereqTests({ rereqAddRemoveRequest: { tests: conceptCodes } })
                            : cartApiClient.addToCart1({
                                cartAddRequest: __assign(__assign(__assign({ concepts: conceptCodes }, (isCxRetainCustomPanelContextEnabled && { orderContext: orderContext })), (isCxCustomRnaHcxEnabled &&
                                    isParentRnaAnalysisAvailable && { expandWithRna: isParentRnaAnalysisSelected })), options),
                            });
                    })];
                case 1:
                    response = _h.sent();
                    isPartnershipRemoved = (((_c = (_b = (_a = response === null || response === void 0 ? void 0 : response.meta) === null || _a === void 0 ? void 0 : _a.partnership) === null || _b === void 0 ? void 0 : _b.selectedPartnerships) === null || _c === void 0 ? void 0 : _c.length) || 0) <
                        (((_e = (_d = meta === null || meta === void 0 ? void 0 : meta.partnership) === null || _d === void 0 ? void 0 : _d.selectedPartnerships) === null || _e === void 0 ? void 0 : _e.length) || 0);
                    if (!partnershipToAdd && isPartnershipRemoved) {
                        showPartnershipWarning(PARTNERSHIP_CODE_REMOVED_MESSAGE);
                    }
                    setNewCartState(response);
                    if (response.errors) {
                        logEvent({
                            eventName: AnalyticsEventName.AddToCartErrors,
                            eventProperties: __assign({ cartProductTypes: (((_f = response.cart) === null || _f === void 0 ? void 0 : _f.orderGroups) || []).map(function (orderGroup) { return orderGroup.productType; }), cartTestSelections: (((_g = response.cart) === null || _g === void 0 ? void 0 : _g.orderGroups) || []).flatMap(function (orderGroup) {
                                    return (orderGroup.testSelections || []).map(function (selection) { return selection.code || ''; });
                                }) }, response.errors),
                        });
                    }
                    if (!response.errors && !isEqual(cart, response.cart)) {
                        setShouldKeepMiniCartOpen(true);
                    }
                    return [2 /*return*/, response];
            }
        });
    }); }, [
        cart,
        cartApiClient,
        isCxCustomRnaHcxEnabled,
        isParentRnaAnalysisAvailable,
        isParentRnaAnalysisSelected,
        meta,
        partnershipToAdd,
        rereqApiClient,
        setNewCartState,
        safelyCallApiClient,
        showPartnershipWarning,
    ]);
    var removeFromCart = React__default.useCallback(function (concepts, removedRRS, 
    /** @deprecated tiered panel and custom core concepts are no longer supported */
    shouldRemoveCustomCoreConceptsOnly, options) { return __awaiter(void 0, void 0, void 0, function () {
        var response, partnerships;
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return __generator(this, function (_j) {
            switch (_j.label) {
                case 0: return [4 /*yield*/, safelyCallApiClient(function () {
                        return cartApiClient.removeFromCart({
                            cartRemoveRequest: __assign(__assign({ concepts: concepts, removedRRS: removedRRS, shouldRemoveCustomCoreConceptsOnly: shouldRemoveCustomCoreConceptsOnly }, (isCxCustomRnaHcxEnabled && {
                                expandReflexWithRna: isReflexRnaAnalysisAvailable && isReflexRnaAnalysisSelected,
                                expandWithRna: isParentRnaAnalysisAvailable && isParentRnaAnalysisSelected,
                            })), options),
                        });
                    })];
                case 1:
                    response = _j.sent();
                    if (!(!((_a = response.cart) === null || _a === void 0 ? void 0 : _a.orderGroups.length) && ((_d = (_c = (_b = response.meta) === null || _b === void 0 ? void 0 : _b.partnership) === null || _c === void 0 ? void 0 : _c.selectedPartnerships) === null || _d === void 0 ? void 0 : _d.length))) return [3 /*break*/, 3];
                    partnerships = response.meta.partnership.selectedPartnerships;
                    return [4 /*yield*/, removeAllPartnerships(partnerships)];
                case 2:
                    response = _j.sent();
                    _j.label = 3;
                case 3:
                    setNewCartState(response);
                    if ((_f = (_e = response.meta) === null || _e === void 0 ? void 0 : _e.genesSuitableForRemainingGroups) === null || _f === void 0 ? void 0 : _f.length) {
                        setCustomModalProps({
                            fulfillGenesInRemainingGroupsModalProps: {
                                genes: response.meta.genesSuitableForRemainingGroups,
                            },
                        });
                    }
                    if (!((_h = (_g = response.meta) === null || _g === void 0 ? void 0 : _g.exome) === null || _h === void 0 ? void 0 : _h.addOnTargets)) {
                        setHasInvalidExomeAddOnConfiguration(false);
                    }
                    return [2 /*return*/];
            }
        });
    }); }, [
        cartApiClient,
        isCxCustomRnaHcxEnabled,
        isParentRnaAnalysisAvailable,
        isParentRnaAnalysisSelected,
        setNewCartState,
        safelyCallApiClient,
    ]);
    var clearCart = React__default.useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _a = setNewCartState;
                    return [4 /*yield*/, safelyCallApiClient(function () { return cartApiClient.clearCart1(); })];
                case 1:
                    _a.apply(void 0, [_b.sent()]);
                    return [2 /*return*/];
            }
        });
    }); }, [cartApiClient, setNewCartState, setRequestPending, safelyCallApiClient]);
    var clearCartErrors = React__default.useCallback(function () {
        setErrors(undefined);
    }, [setErrors]);
    var setReflexOptions = React__default.useCallback(function (options) { return __awaiter(void 0, void 0, void 0, function () {
        var error, shouldResetError, updatedErrors, _a;
        var _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    error = reflexValidationErrors[options.rereqSetCode];
                    if (error) {
                        error.conditionMissing = false;
                        shouldResetError = !(error === null || error === void 0 ? void 0 : error.noTestsSelected);
                        updatedErrors = __assign(__assign({}, reflexValidationErrors), (_b = {}, _b[options.rereqSetCode] = shouldResetError ? undefined : error, _b));
                        setReflexValidationErrors(updatedErrors);
                    }
                    _a = setNewCartState;
                    return [4 /*yield*/, safelyCallApiClient(function () { return reflexApiClient.setReflexOptions({ reflexSetOptionsRequest: options }); })];
                case 1:
                    _a.apply(void 0, [_c.sent()]);
                    return [2 /*return*/];
            }
        });
    }); }, [reflexApiClient, reflexValidationErrors, setNewCartState, safelyCallApiClient]);
    var searchReflexTests = React__default.useCallback(debounce(function (rereqSetCode, query) { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 5, , 6]);
                    if (!isCxCustomRnaHcxEnabled) return [3 /*break*/, 2];
                    return [4 /*yield*/, reflexApiClient.searchReflexTests({
                            isParentRnaAnalysisSelected: isParentRnaAnalysisSelected,
                            isReflexRnaAnalysisSelected: isReflexRnaAnalysisSelected,
                            query: query,
                            rereqSetCode: rereqSetCode,
                        })];
                case 1:
                    _a = _b.sent();
                    return [3 /*break*/, 4];
                case 2: return [4 /*yield*/, reflexApiClient.searchReflexTests({ query: query, rereqSetCode: rereqSetCode })];
                case 3:
                    _a = _b.sent();
                    _b.label = 4;
                case 4: return [2 /*return*/, _a];
                case 5:
                    _b.sent();
                    return [2 /*return*/, []];
                case 6: return [2 /*return*/];
            }
        });
    }); }, FETCH_DEBOUNCE_DELAY), [isParentRnaAnalysisSelected, isReflexRnaAnalysisSelected, reflexApiClient]);
    var addReflexTests = React__default.useCallback(function (rereqSetCode, tests, options) { return __awaiter(void 0, void 0, void 0, function () {
        var error, shouldResetError, _a;
        var _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    error = reflexValidationErrors[rereqSetCode];
                    if (error) {
                        error.noTestsSelected = false;
                        shouldResetError = !(error === null || error === void 0 ? void 0 : error.conditionMissing);
                        setReflexValidationErrors(__assign(__assign({}, reflexValidationErrors), (_b = {}, _b[rereqSetCode] = shouldResetError ? undefined : error, _b)));
                    }
                    _a = setNewCartState;
                    return [4 /*yield*/, safelyCallApiClient(function () {
                            return reflexApiClient.addReflexTests({
                                reflexAddRemoveRequest: __assign(__assign({ rereqSetCode: rereqSetCode, tests: tests }, (isCxCustomRnaHcxEnabled &&
                                    isReflexRnaAnalysisAvailable && { expandWithRna: isReflexRnaAnalysisSelected })), options),
                            });
                        })];
                case 1:
                    _a.apply(void 0, [_c.sent()]);
                    return [2 /*return*/];
            }
        });
    }); }, [
        isCxCustomRnaHcxEnabled,
        isReflexRnaAnalysisAvailable,
        isReflexRnaAnalysisSelected,
        reflexApiClient,
        reflexValidationErrors,
        safelyCallApiClient,
        setNewCartState,
    ]);
    var removeReflexTests = React__default.useCallback(function (rereqSetCode, tests, options) { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _a = setNewCartState;
                    return [4 /*yield*/, safelyCallApiClient(function () {
                            return reflexApiClient.removeReflexTests({
                                reflexAddRemoveRequest: __assign(__assign({ rereqSetCode: rereqSetCode, tests: tests }, (isCxCustomRnaHcxEnabled &&
                                    isReflexRnaAnalysisAvailable && { expandWithRna: isReflexRnaAnalysisSelected })), options),
                            });
                        })];
                case 1:
                    _a.apply(void 0, [_b.sent()]);
                    return [2 /*return*/];
            }
        });
    }); }, [
        isCxCustomRnaHcxEnabled,
        isReflexRnaAnalysisAvailable,
        isReflexRnaAnalysisSelected,
        reflexApiClient,
        safelyCallApiClient,
        setNewCartState,
    ]);
    var clearReflexCustomizedSelection = React__default.useCallback(function (rereqSetCode) { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _a = setNewCartState;
                    return [4 /*yield*/, safelyCallApiClient(function () { return reflexApiClient.clearReflexCustomizedSelection({ rereqSetCode: rereqSetCode }); })];
                case 1:
                    _a.apply(void 0, [_b.sent()]);
                    return [2 /*return*/];
            }
        });
    }); }, [reflexApiClient, setNewCartState, safelyCallApiClient]);
    var addRereqTests = React__default.useCallback(function (tests) { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    setRereqValidationError(false);
                    _a = setNewCartState;
                    return [4 /*yield*/, safelyCallApiClient(function () { return rereqApiClient.addRereqTests({ rereqAddRemoveRequest: { tests: tests } }); })];
                case 1:
                    _a.apply(void 0, [_b.sent()]);
                    return [2 /*return*/];
            }
        });
    }); }, [rereqApiClient, setNewCartState, setRereqValidationError, safelyCallApiClient]);
    var removeRereqTests = React__default.useCallback(function (tests) { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _a = setNewCartState;
                    return [4 /*yield*/, safelyCallApiClient(function () { return rereqApiClient.removeRereqTests({ rereqAddRemoveRequest: { tests: tests } }); })];
                case 1:
                    _a.apply(void 0, [_b.sent()]);
                    return [2 /*return*/];
            }
        });
    }); }, [rereqApiClient, setNewCartState, safelyCallApiClient]);
    var clearRereqCustomizedSelection = React__default.useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _a = setNewCartState;
                    return [4 /*yield*/, safelyCallApiClient(function () { return rereqApiClient.clearRereqCustomizedSelection(); })];
                case 1:
                    _a.apply(void 0, [_b.sent()]);
                    return [2 /*return*/];
            }
        });
    }); }, [rereqApiClient, setNewCartState, safelyCallApiClient]);
    var searchRereqTests = React__default.useCallback(debounce(function (query) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, rereqApiClient.searchRereqTests({ query: query })];
                case 1: return [2 /*return*/, _a.sent()];
                case 2:
                    _a.sent();
                    return [2 /*return*/, []];
                case 3: return [2 /*return*/];
            }
        });
    }); }, FETCH_DEBOUNCE_DELAY), [rereqApiClient]);
    var addPartnerships = React__default.useCallback(function (value) { return __awaiter(void 0, void 0, void 0, function () {
        var response, responsePartnerships, responsePartnershipsLength, currentCartPartnershipsLength, isPartnershipNotAdded, isPartnershipRemoved;
        var _a, _b, _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0: return [4 /*yield*/, safelyCallApiClient(function () {
                        return partnershipApiClient.addPartnerships({ partnershipAddRemoveRequest: { partnerships: [value] } });
                    })];
                case 1:
                    response = _e.sent();
                    responsePartnerships = (_b = (_a = response === null || response === void 0 ? void 0 : response.meta) === null || _a === void 0 ? void 0 : _a.partnership) === null || _b === void 0 ? void 0 : _b.selectedPartnerships;
                    responsePartnershipsLength = responsePartnerships === null || responsePartnerships === void 0 ? void 0 : responsePartnerships.length;
                    currentCartPartnershipsLength = (_d = (_c = meta === null || meta === void 0 ? void 0 : meta.partnership) === null || _c === void 0 ? void 0 : _c.selectedPartnerships) === null || _d === void 0 ? void 0 : _d.length;
                    isPartnershipNotAdded = responsePartnershipsLength !== undefined &&
                        currentCartPartnershipsLength !== undefined &&
                        responsePartnershipsLength === currentCartPartnershipsLength &&
                        (responsePartnerships === null || responsePartnerships === void 0 ? void 0 : responsePartnerships.every(function (partnership) { return partnership.value !== value; }));
                    if (isPartnershipNotAdded) {
                        showPartnershipWarning(PARTNERSHIP_CODE_NOT_ADDED_MESSAGE);
                    }
                    isPartnershipRemoved = (responsePartnershipsLength || 0) < (currentCartPartnershipsLength || 0);
                    if (isPartnershipRemoved) {
                        showPartnershipWarning(PARTNERSHIP_CODE_REMOVED_MESSAGE);
                    }
                    setNewCartState(response);
                    return [2 /*return*/];
            }
        });
    }); }, [partnershipApiClient, meta, setNewCartState, safelyCallApiClient, showPartnershipWarning]);
    var searchPartnerships = React__default.useCallback(
    // debounce returns DebouncedFunc<(query: string) => Promise<PartnershipOption[]>> but you can't pass it to AsyncSelect
    debounce(function (query) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, partnershipApiClient.searchPartnerships({ query: query })];
                case 1: return [2 /*return*/, _a.sent()];
                case 2:
                    _a.sent();
                    return [2 /*return*/, []];
                case 3: return [2 /*return*/];
            }
        });
    }); }, FETCH_DEBOUNCE_DELAY), [partnershipApiClient]);
    var removePartnerships = React__default.useCallback(function (value) { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _a = setNewCartState;
                    return [4 /*yield*/, safelyCallApiClient(function () {
                            return partnershipApiClient.removePartnerships({ partnershipAddRemoveRequest: { partnerships: [value] } });
                        })];
                case 1:
                    _a.apply(void 0, [_b.sent()]);
                    setShouldKeepMiniCartOpen(true);
                    return [2 /*return*/];
            }
        });
    }); }, [partnershipApiClient, setNewCartState, safelyCallApiClient]);
    var removeAllPartnerships = React__default.useCallback(function (values) { return __awaiter(void 0, void 0, void 0, function () {
        var partnershipValues, response;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    partnershipValues = values.map(function (item) { return item.value; });
                    return [4 /*yield*/, safelyCallApiClient(function () {
                            return partnershipApiClient.removePartnerships({
                                partnershipAddRemoveRequest: { partnerships: partnershipValues },
                            });
                        })];
                case 1:
                    response = _a.sent();
                    return [2 /*return*/, response];
            }
        });
    }); }, [partnershipApiClient, safelyCallApiClient]);
    var closeOpenedMiniCart = React__default.useCallback(function () {
        setShouldKeepMiniCartOpen(false);
    }, [setShouldKeepMiniCartOpen]);
    var saveCustomPanel = React__default.useCallback(function (concepts, name) { return __awaiter(void 0, void 0, void 0, function () {
        var response, e_2, error, errorContent;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, 4, 5]);
                    setRequestPending(true);
                    return [4 /*yield*/, cartApiClient.createCustomPanel({ customPanel: { concepts: concepts, name: name } })];
                case 1:
                    response = _a.sent();
                    setNewCartState((response.cart || {}));
                    return [2 /*return*/, response];
                case 2:
                    e_2 = _a.sent();
                    error = e_2;
                    return [4 /*yield*/, error.response.json()];
                case 3:
                    errorContent = _a.sent();
                    return [2 /*return*/, errorContent];
                case 4:
                    setRequestPending(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [cartApiClient, setNewCartState]);
    var toggleCustomModalForm = React__default.useCallback(function (props) {
        setCustomModalProps(props);
    }, [setCustomModalProps]);
    var configureExomeAddOn = React__default.useCallback(function (targets) { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    setHasInvalidExomeAddOnConfiguration(false);
                    _a = setNewCartState;
                    return [4 /*yield*/, safelyCallApiClient(function () {
                            var _a;
                            return cartApiClient.configureExomeAddOn1({
                                exomeAddOnTargets: __assign(__assign({}, (_a = meta === null || meta === void 0 ? void 0 : meta.exome) === null || _a === void 0 ? void 0 : _a.addOnTargets), targets),
                            });
                        })];
                case 1:
                    _a.apply(void 0, [_b.sent()]);
                    return [2 /*return*/];
            }
        });
    }); }, [meta]);
    React__default.useEffect(function () {
        if (!isUserAuth)
            return;
        if (options.getCart) {
            options.getCart();
        }
        else {
            getCart();
        }
    }, [isUserAuth]);
    return __assign(__assign(__assign({ 
        // Add tests
        addPartnerships: addPartnerships, addReflexTests: addReflexTests, addRereqTests: addRereqTests, addToCart: addToCart, cart: cart, clearCart: clearCart, clearCartErrors: clearCartErrors, clearReflexCustomizedSelection: clearReflexCustomizedSelection, clearRereqCustomizedSelection: clearRereqCustomizedSelection, closeOpenedMiniCart: closeOpenedMiniCart, configureExomeAddOn: configureExomeAddOn, customModalProps: customModalProps, errors: errors, featureFlags: featureFlags, getCart: getCart, gotoCartPage: gotoCartPage, goToSignInPage: goToSignInPage, hasInvalidExomeAddOnConfiguration: hasInvalidExomeAddOnConfiguration }, (isCxCustomRnaHcxEnabled && {
        isParentRnaAnalysisAvailable: isParentRnaAnalysisAvailable,
        isParentRnaAnalysisSelected: isParentRnaAnalysisSelected,
        isReflexRnaAnalysisAvailable: isReflexRnaAnalysisAvailable,
        isReflexRnaAnalysisSelected: isReflexRnaAnalysisSelected,
    })), { isUserAuthenticated: isUserAuth, meta: meta, partnershipToAdd: partnershipToAdd, partnershipWarning: partnershipWarning, reflexValidationErrors: reflexValidationErrors, removeAllPartnerships: removeAllPartnerships, removeFromCart: removeFromCart, removePartnerships: removePartnerships, removeReflexTests: removeReflexTests, removeRereqTests: removeRereqTests, requestPending: requestPending, rereqValidationError: rereqValidationError, saveCustomPanel: saveCustomPanel, searchPartnerships: searchPartnerships, searchReflexTests: searchReflexTests, searchRereqTests: searchRereqTests, setHasInvalidExomeAddOnConfiguration: setHasInvalidExomeAddOnConfiguration, setPartnershipToAdd: setPartnershipToAdd, setReflexOptions: setReflexOptions, setReflexValidationErrors: setReflexValidationErrors, setRereqValidationError: setRereqValidationError, setShouldKeepMiniCartOpen: setShouldKeepMiniCartOpen, shouldKeepMiniCartOpen: shouldKeepMiniCartOpen, shouldShowPartnershipWarning: shouldShowPartnershipWarning, showLoadingOverlay: setRequestPending, showPatientInfoBanner: showPatientInfoBanner, toggleCustomModalForm: toggleCustomModalForm, user: portalUser }), options);
};
/**
 * The Cart Context.
 */
var CartContext = React__default.createContext(copyDefaultContext());

export { CartContext, FETCH_DEBOUNCE_DELAY, PARTNERSHIP_WARNING_DISPLAY_TIME, PARTNERSHIP_WARNING_TRANSITION_DURATION, useMakeCartContext };
//# sourceMappingURL=CartContext.esm.js.map
